import React, { useMemo, useEffect } from "react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { z } from "zod";
import { Ticket } from "@/hooks/use-event-data";
import { MultiEmailInput } from "./MultiEmailInput";

// Define the schema for the CustomField
interface CustomField {
  id: string;
  label: string;
  type: string;
  required: boolean;
}

interface TicketType {
  id: string;
  name: string;
  price: number;
  description: string;
}

// Registration schema for typing
const registrationSchema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  // email: z.string().email(),
  phone: z.string().optional(),
  ticketType: z.string().min(1),
  quantity: z.coerce.number().min(1),
  // company: z.string().min(2),
  // jobTitle: z.string().min(2),
  // dietary: z.string().optional(),
  // questions: z.string().optional(),
  marketingConsent: z.boolean().optional(),
  // agreeTerms: z.boolean(),
  agreeTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms and conditions",
  }),
  email: z.array(
    z.string().email("One or more recipients are not valid emails")
  ),
  // .nonempty("At least one recipient is required"),
});

type RegistrationFormValues = z.infer<typeof registrationSchema>;

interface RegistrationFormProps {
  form: UseFormReturn<RegistrationFormValues>;
  ticketTypes: Ticket[];
  // customFields: CustomField[];
  loader: boolean;
  emails: string[];
  setEmails: React.Dispatch<React.SetStateAction<string[]>>;

  // onContinue: () => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  form,
  ticketTypes,
  // customFields,
  loader,
  emails,
  setEmails,
  // onContinue,
}) => {
  const selectedTicketName = useWatch({
    control: form.control,
    name: "ticketType",
  });

  const maxQuantity = useMemo(() => {
    const selected = ticketTypes?.find(
      (t) => t.ticket_name === selectedTicketName
    );
    return selected ? parseInt(selected.max_quantity || "5") : 5;
  }, [selectedTicketName, ticketTypes]);

  useEffect(() => {
    form.setValue("email", emails, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [emails, form]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Attendee Information</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name *</FormLabel>
              <FormControl>
                <Input placeholder="First Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name *</FormLabel>
              <FormControl>
                <Input placeholder="Last Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        /> */}

        <FormField
          control={form.control}
          name="quantity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity *</FormLabel>
              <Select
                onValueChange={(value) => field.onChange(parseInt(value))}
                defaultValue={field.value.toString()}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(
                    (num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num}
                      </SelectItem>
                    )
                  )}
                  {/* {[1, 2, 3, 4, 5].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num}
                  </SelectItem>
                ))} */}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="Phone Number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator className="my-4" />

      {ticketTypes && ticketTypes.length > 0 && (
        <>
          <h2 className="text-xl font-semibold">Ticket Selection</h2>
          <FormField
            control={form.control}
            name="ticketType"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Select Ticket Type *</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                    defaultValue={field.value}
                    className="space-y-3"
                  >
                    {ticketTypes?.map((ticket) => (
                      <div
                        key={ticket.ticket_name}
                        className={`flex items-center justify-between space-x-2 rounded-md border p-4 ${
                          field.value === ticket.ticket_name
                            ? "border-cecure-700 bg-cecure-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-start">
                          <RadioGroupItem
                            value={ticket.ticket_name}
                            id={`ticket-${ticket.ticket_name}`}
                          />
                          <div className="ml-3 space-y-1">
                            <label
                              htmlFor={`ticket-${ticket.ticket_name}`}
                              className="font-medium cursor-pointer"
                            >
                              {ticket.ticket_name}
                            </label>
                            <p className="text-sm text-gray-500">
                              {ticket.description}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">${ticket.price}</div>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
      <FormField
        control={form.control}
        name="email"
        render={() => (
          <FormItem>
            <FormLabel>Recipients *</FormLabel>
            <FormControl>
              <MultiEmailInput emails={emails} setEmails={setEmails} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* <FormField
        control={form.control}
        name="quantity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Quantity *</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(parseInt(value))}
              defaultValue={field.value.toString()}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Quantity" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(
                  (num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num}
                    </SelectItem>
                  )
                )}
               
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      /> */}

      {/* <Separator className="my-4" />

      <h2 className="text-xl font-semibold">Additional Information</h2>

      {customFields.map((field) => (
        <FormField
          key={field.id}
          control={form.control}
          name={field.id as any}
          render={({ field: formField }) => (
            <FormItem>
              <FormLabel>
                {field.label}
                {field.required ? " *" : ""}
              </FormLabel>
              <FormControl>
                {field.type === "textarea" ? (
                  <Textarea placeholder={field.label} {...formField} />
                ) : (
                  <Input placeholder={field.label} {...formField} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))} */}

      <Separator className="my-4" />

      <FormField
        control={form.control}
        name="marketingConsent"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 bg-gray-50">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                I agree to receive marketing communications from this event
                organizer
              </FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="agreeTerms"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 bg-gray-50">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                I agree to the terms and conditions of this event *
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={loader}
          // onClick={onContinue}
        >
          Continue to Payment
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RegistrationForm;
